import * as React from 'react';
import { CoreRadioButton } from '../internal/WixUiCoreComponents/components/CoreRadioButton';
import { CoreCheckbox } from '../internal/WixUiCoreComponents/components/CoreCheckbox';
import { classes, st } from './Option.st.css';
import {
  BOX_SELECTION_DATA_ATTRIBUTES,
  BOX_SELECTION_DATA_HOOKS,
} from './dataHooks';
import classnames from 'classnames';
import { NativeSelectorConsumer } from './NativeSelector';
import {
  BoxSelectionOptionProps,
  BoxSelectionOptionState,
  BoxSelectionSelectionType,
} from './types';
import { shellClasses } from '../../common/shellClasses';

/** Option */
export class Option extends React.Component<
  BoxSelectionOptionProps,
  BoxSelectionOptionState
> {
  static defaultProps: Partial<BoxSelectionOptionProps> = {
    checked: false,
    disabled: false,
    unavailable: false,
  };

  private inputRef: CoreCheckbox | CoreRadioButton;

  state: BoxSelectionOptionState = {
    focused: false,
  };

  focus() {
    this.inputRef?.focus();
  }

  _getDataAttributes(nativeSelector: BoxSelectionSelectionType) {
    const { checked, disabled, unavailable } = this.props;
    const { focused } = this.state;
    return {
      [BOX_SELECTION_DATA_ATTRIBUTES.CHECKED]: checked,
      [BOX_SELECTION_DATA_ATTRIBUTES.DISABLED]: disabled,
      [BOX_SELECTION_DATA_ATTRIBUTES.UNAVAILABLE]: unavailable,
      [BOX_SELECTION_DATA_ATTRIBUTES.FOCUSED]: focused,
      [BOX_SELECTION_DATA_ATTRIBUTES.NATIVE_SELECTOR]: nativeSelector,
    };
  }

  _onFocus = () => {
    this.setState({ focused: true });
  };

  _onBlur = () => {
    this.setState({ focused: false });
  };

  _onChange = () => {
    const { id, onChange } = this.props;

    if (onChange) {
      onChange({ id });
    }
  };
  _getAriaLabel = () => {
    const { children } = this.props;
    return this.props['aria-label']
      ? this.props['aria-label']
      : typeof children === 'string'
      ? children
      : null;
  };

  _getCoreElement = (nativeSelector: BoxSelectionSelectionType) => {
    const {
      children,
      id,
      name,
      checked,
      disabled,
      unstableHasInnerWrapper,
      required,
    } = this.props;
    const focusedClassName = classnames(
      shellClasses.hasCustomFocus,
      this.state.focused && classes.focused,
    );

    return nativeSelector === BoxSelectionSelectionType.CHECKBOX ? (
      <CoreCheckbox
        data-hook={BOX_SELECTION_DATA_HOOKS.BOX_SELECTION_OPTION}
        name={name}
        checked={checked}
        value={id}
        onChange={this._onChange}
        id={id}
        onFocusByKeyboard={this._onFocus}
        onBlur={this._onBlur}
        disabled={disabled}
        aria-describedBy={`description-${id}`}
        aria-label={this._getAriaLabel()}
        className={classnames(classes.wrapper, focusedClassName)}
        ref={(ref) => (this.inputRef = ref)}
      >
        {children}
      </CoreCheckbox>
    ) : (
      <CoreRadioButton
        data-hook={BOX_SELECTION_DATA_HOOKS.BOX_SELECTION_OPTION}
        name={name}
        label={children}
        checked={checked}
        value={id}
        onChange={this._onChange}
        id={id}
        onFocusByKeyboard={this._onFocus}
        onBlur={this._onBlur}
        disabled={disabled}
        aria-describedBy={`description-${id}`}
        required={required}
        aria-label={this._getAriaLabel()}
        className={classnames(classes.wrapper, focusedClassName)}
        ref={(ref) => (this.inputRef = ref)}
        unstableHasInnerWrapper={unstableHasInnerWrapper}
      />
    );
  };

  render() {
    const {
      id,
      checked,
      disabled,
      unavailable,
      description,
      size,
      className,
      vertical,
      type,
    } = this.props;

    return (
      <NativeSelectorConsumer>
        {(nativeSelector) => (
          <div
            className={st(
              classes.root,
              {
                size,
                checked,
                disabled,
                unavailable,
              },
              classnames(className, {
                [classes.vertical]: vertical,
              }),
            )}
            data-id={id}
            data-hook={BOX_SELECTION_DATA_HOOKS.BOX_SELECTION_OPTION_WRAPPER}
            {...this._getDataAttributes(type ?? nativeSelector)}
          >
            {unavailable && (
              <svg className={classes.unavailableDiagonalLine}>
                <line x1="0" y1="100%" x2="100%" y2="0" />
              </svg>
            )}
            {description && (
              <span className={classes.hide} id={`description-${id}`}>
                {description}
              </span>
            )}
            {this._getCoreElement(type ?? nativeSelector)}
          </div>
        )}
      </NativeSelectorConsumer>
    );
  }
}
